import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Github } from '@/components/icons';
import { Button } from '@/components/ui/button';
import type { LoginShow } from '@/types';

// The hacker sign-in page: app/(hack)/login/layout.tsx (the theme wrapper),
// page.tsx (the Suspense shell, which only ever showed while the client component
// hydrated) and login-form.tsx, as one component.
export default function Show({ error }: LoginShow) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (error) {
      toast.error(error.replace(/_/g, ' '));
    }
  }, [error]);

  // The same call the better-auth client made: the endpoint hands back the URL that
  // starts the OAuth flow and remembers where a failure should land.
  const handleGithubLogin = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/auth/sign-in/social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'github',
          callbackURL: '/hacker',
          errorCallbackURL: '/login?error=unauthorized',
        }),
      });
      const body = (await response.json()) as { url?: string };

      if (!body.url) {
        throw new Error('Sign-in failed');
      }

      window.location.href = body.url;
    } catch (signInError) {
      toast.error('Error al iniciar sesión con GitHub');
      console.error(signInError);
      setIsLoading(false);
    }
  };

  return (
    <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
      <div className="flex min-h-screen flex-col bg-background px-4 py-12">
        {/* Main content */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-md space-y-8">
            {/* Title */}
            <div className="text-center">
              <h1 className="inline-block font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
                Iniciar{' '}
                <span className="bg-primary px-2 py-1 text-background">
                  Sesión
                </span>
              </h1>
              <p className="mt-4 font-mono text-primary/70 text-sm">
                Ingresa a tu cuenta de hacker
              </p>
            </div>

            {/* Login Card */}
            <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
              <div className="space-y-6">
                <p className="text-center font-mono text-primary/80 text-sm">
                  Para acceder a tu perfil de hacker, inicia sesión con tu
                  cuenta de GitHub.
                </p>

                <Button
                  onClick={handleGithubLogin}
                  disabled={isLoading}
                  size="lg"
                  className="w-full"
                >
                  <Github className="mr-2 h-5 w-5" />
                  {isLoading ? 'Conectando...' : 'Iniciar sesión con GitHub'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Platanus logo footer */}
        <div className="mt-16 flex justify-center">
          <div
            className="aspect-[576/112] h-7 w-auto sm:h-9 md:h-10"
            style={{
              backgroundColor: 'hsl(var(--primary))',
              maskImage: 'url(/assets/logos/platanus.svg)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskImage: 'url(/assets/logos/platanus.svg)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
            }}
          />
        </div>
      </div>
    </div>
  );
}
